import { motion } from "framer-motion";
import { type FC, useEffect, useState } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import ModuleIcon from "./shared/icon_module.svg";
import ChevronRightIcon from "./shared/right_chevron.svg";
import { groupLabel } from "./CircleFlowBar";
import { useEditorState, type CircleFlowGroup, type CircleFlowSubcircle } from "@/store/editorState";
import { classNames, localizeCircleFlowText, postMessageToRef } from "@/utils";

export const CircleFlowSideBar: FC = () => {
  const { circleFlow, godotContainerRef, collapsedSidebar, setCollapsedSidebar, metadata, language, achievements, parseAchievementId } =
    useEditorState();

  const lectureName =
    language === "tr"
      ? (metadata?.cms?.lecture_name_tr ?? "")
      : (metadata?.cms?.lecture_name_en ?? "");

  const [openedGroups, setOpenedGroups] = useState<number[]>([]);
  const [pendingNavigateIndex, setPendingNavigateIndex] = useState<number | null>(null);

  const { t } = useTranslation();

  const activeGroup =
    circleFlow && circleFlow.length > 0
      ? (circleFlow.find((g) => g.active) ?? circleFlow[0])
      : null;

  useEffect(() => {
    if (activeGroup && !openedGroups.includes(activeGroup.group_index)) {
      setOpenedGroups((prev) => [...prev, activeGroup.group_index]);
    }
    // eslint-disable-next-line
  }, [activeGroup?.group_index]);

  useEffect(() => {
    if (pendingNavigateIndex === null || !circleFlow) return;
    const resolved = circleFlow.some((group) =>
      group.subcircles.some((sub) => sub.index === pendingNavigateIndex && sub.selected),
    );
    if (resolved) {
      setPendingNavigateIndex(null);
    }
  }, [circleFlow, pendingNavigateIndex]);

  useEffect(() => {
    if (pendingNavigateIndex === null) return;
    const timeout = setTimeout(() => setPendingNavigateIndex(null), 30000);
    return () => clearTimeout(timeout);
  }, [pendingNavigateIndex]);

  if (!circleFlow || circleFlow.length === 0 || !activeGroup) return null;

  // When there's only one group, the group number/panel is meaningless, so we
  // render a flat list of sub-steps instead (mirrors CircleFlowBar's hasSingleGroup case).
  const hasSingleGroup = circleFlow.length === 1;

  const flatSubcircles = circleFlow.flatMap((group) =>
    group.subcircles.map((sub) => ({ sub, group })),
  );
  const currentFlatIndex = flatSubcircles.findIndex(({ sub }) => sub.selected);

  // Checkpoint outline: grey if the checkpoint isn't achieved yet, bright
  // green once it is. Only shown for non-locked subcircles that actually
  // carry a checkpoint. Thicker than a plain border so it reads as a
  // distinct checkpoint indicator (mirrors CircleFlowBar).
  const getCheckpointBorderClass = (sub: CircleFlowSubcircle) => {
    if (sub.locked || sub.checkpoint_id === null || sub.checkpoint_id === undefined) return "";
    const achieved =
      achievements?.some(
        (achievement) =>
          (String(achievement.id) === String(parseAchievementId(sub.checkpoint_id))) &&
          achievement.is_achieved,
      ) ?? false;
    return achieved ? "border-4 border-[#03FF48]" : "border-4 border-[#D5D5D5]";
  };

  const getSubcircleShapeClass = (type?: string) => {
    const icon = type?.toLowerCase();
    if (icon === "code") {
      return "rounded-[4px] rotate-45";
    }
    if (icon === "qa" || icon === "q&a") {
      return "rounded-md";
    }
    return "rounded-full";
  };

  const toggleOpenGroup = (groupIndex: number) => {
    setOpenedGroups((prev) =>
      prev.includes(groupIndex) ? prev.filter((item) => item !== groupIndex) : [...prev, groupIndex],
    );
  };

  const navigateTo = (sub: CircleFlowSubcircle) => {
    if (sub.locked || pendingNavigateIndex !== null) return;
    setPendingNavigateIndex(sub.index);
    requestAnimationFrame(() => {
      postMessageToRef(godotContainerRef, {
        action: "circleFlowNavigate",
        index: sub.index,
      });
    });
  };

  const goToPrevious = () => {
    if (currentFlatIndex > 0) {
      const target = flatSubcircles[currentFlatIndex - 1];
      setOpenedGroups((prev) =>
        prev.includes(target.group.group_index) ? prev : [...prev, target.group.group_index],
      );
      navigateTo(target.sub);
    }
  };

  const goToNext = () => {
    if (currentFlatIndex >= 0 && currentFlatIndex < flatSubcircles.length - 1) {
      const target = flatSubcircles[currentFlatIndex + 1];
      setOpenedGroups((prev) =>
        prev.includes(target.group.group_index) ? prev : [...prev, target.group.group_index],
      );
      navigateTo(target.sub);
    }
  };

  const collapseSidebar = () => {
    setCollapsedSidebar(true);
  };

  const onAnimationComplete = () => {
    const sidebar = document.getElementById("circleflow-sidebar");
    if (collapsedSidebar) {
      sidebar?.style.setProperty("display", "none");
    }
  };

  const onAnimationStart = () => {
    const sidebar = document.getElementById("circleflow-sidebar");
    if (!collapsedSidebar) {
      sidebar?.style.setProperty("display", "initial");
    }
  };

  return (
    <div id="circleflow-side-bar-container" className="flex flex-col">
      {collapsedSidebar && (
        <button
          onClick={() => setCollapsedSidebar(false)}
          className="bg-[#C3D8F3] dark:bg-vs-dark-background-lighter text-vs-dark-background dark:text-vs-dark-text-lighter h-8 w-5 rounded-md flex items-center justify-center mt-7"
        >
          <img
            src={ChevronRightIcon}
            alt="module icon"
            className="object-contain w-5 h-5 dark:invert dark:brightness-0"
          />
        </button>
      )}
      <motion.div
        id="circleflow-sidebar"
        initial={{ width: 250, opacity: 1 }}
        animate={{
          width: !collapsedSidebar ? 250 : 0,
          opacity: collapsedSidebar ? 0 : 1,
        }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ ease: "easeInOut", duration: 0.2 }}
        onAnimationComplete={onAnimationComplete}
        onAnimationStart={onAnimationStart}
        className="relative h-full w-[250px] pt-6 pb-0 bg-white dark:bg-vs-dark-background-lighter rounded-[30px] text-editor-text flex-none overflow-hidden"
      >
        {!collapsedSidebar && (
          <motion.div
            key="expandedContent"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="overflow-y-auto h-full pb-24 px-4"
          >
            <div className="side-bar bg-white dark:bg-vs-dark-background-lighter dark:text-vs-dark-text sticky top-0 z-[999] mb-4">
              <div className="flex items-start justify-between">
                <div className="w-11/12 pb-4">
                  {lectureName && (
                    <p className="text-editor-title dark:text-vs-dark-text pl-2 text-[1.1rem] font-alata">
                      {lectureName}
                    </p>
                  )}
                </div>
                <button
                  className="w-8 h-8 bg-editor-background dark:bg-vs-dark-background rounded-md flex items-center justify-center"
                  onClick={collapseSidebar}
                >
                  <img
                    src={ModuleIcon}
                    alt="module icon"
                    className="object-contain w-5 h-5 dark:invert dark:brightness-0"
                  />
                </button>
              </div>
              {lectureName && <hr className="border-t border-[#E3E8F1] dark:border-vs-dark-background" />}
            </div>
            <ol className="space-y-4">
              {hasSingleGroup ? (
                activeGroup.subcircles.map((sub) => (
                  <li
                    key={sub.index}
                    className={classNames(
                      sub.selected
                        ? "bg-[#E3F0FF] dark:bg-vs-dark-background"
                        : "bg-transparent dark:hover:text-opacity-60",
                      sub.locked
                        ? "cursor-default text-gray-400 dark:text-gray-500"
                        : sub.visited && !sub.selected
                          ? "cursor-pointer text-riders-green"
                          : "cursor-pointer text-editor-title dark:text-vs-dark-text-lighter hover:text-riders-green",
                      "text-base leading-6 font-medium py-2 px-3 space-x-2 rounded-[16px] duration-200 flex items-center"
                    )}
                    onClick={() => navigateTo(sub)}
                  >
                    <span className="w-8 h-8 flex-none flex items-center justify-center">
                      <span
                        className={classNames(
                          sub.selected
                            ? "bg-[#FFB81C]"
                            : sub.visited || sub.completed
                              ? "bg-riders-green"
                              : sub.locked
                                ? "bg-gray-400"
                                : "bg-editor-blue",
                          getSubcircleShapeClass(sub.icon),
                          getCheckpointBorderClass(sub),
                          sub.icon?.toLowerCase() === "code" ? "w-[28px] h-[28px]" : "w-8 h-8",
                          "flex items-center justify-center text-white  text-lg"
                        )}
                      >
                        <span className={sub.icon?.toLowerCase() === "code" ? "-rotate-45" : ""}>
                          {sub.stage}
                        </span>
                      </span>
                    </span>
                    <span className="break-words whitespace-normal min-w-0 flex-1">
                      {localizeCircleFlowText(sub.title)}
                    </span>
                  </li>
                ))
              ) : (
                circleFlow.map((group: CircleFlowGroup) => {
                  const isGroupVisited = group.completed || group.subcircles.some((sub) => sub.visited || sub.completed);

                  return (
                    <div
                      key={group.group_index}
                      className={classNames(
                        group.active
                          ? "bg-[#E3F0FF] dark:bg-vs-dark-background"
                          : "bg-editor-chapter-bg dark:bg-[#304057]",
                        "rounded-[30px] relative w-full py-2 px-2"
                      )}
                    >
                      <li
                        className={classNames(
                          "hover:text-riders-green dark:text-[#BCC5E7] cursor-pointer text-editor-title dark:bg-transparent text-base flex items-center space-x-2 font-medium py-2 pl-3 rounded-md duration-200",
                          group.subcircles.length > 1 ? "pr-8" : "pr-3"
                        )}
                        onClick={() => toggleOpenGroup(group.group_index)}
                      >
                        <span
                          className={classNames(
                            group.active
                              ? "bg-[#FFB81C]"
                              : isGroupVisited
                                ? "bg-riders-green"
                                : "bg-editor-blue",
                            "w-9 h-9 text-white flex-none flex justify-center items-center font-bold text-lg rounded-full"
                          )}
                        >
                          <span>
                            {group.group_index}
                          </span>
                        </span>
                        <span className="leading-6 break-words whitespace-normal min-w-0 flex-1">
                          {groupLabel(group)}
                        </span>
                      </li>
                      {(group.subcircles.length <= 1 ||
                        openedGroups.includes(group.group_index)) && (
                          <ol className="space-y-1 mt-2 px-1.5 pb-2">
                            {group.subcircles.map((sub) => (
                              <li
                                key={sub.index}
                                className={classNames(
                                  sub.selected
                                    ? "bg-[#C8E2FF] dark:bg-vs-dark-background-lighter"
                                    : "bg-transparent dark:hover:text-opacity-60",
                                  sub.locked
                                    ? "cursor-default text-gray-400 dark:text-gray-500"
                                    : sub.visited && !sub.selected
                                      ? "cursor-pointer text-riders-green"
                                      : "cursor-pointer text-editor-title dark:text-vs-dark-text-lighter hover:text-riders-green",
                                  "text-sm leading-5 font-medium py-2 px-3 space-x-2 rounded-full duration-200 flex items-center"
                                )}
                                onClick={() => navigateTo(sub)}
                              >
                                <span
                                  className={classNames(
                                    sub.selected
                                      ? "bg-[#FFB81C]"
                                      : sub.visited || sub.completed
                                        ? "bg-riders-green"
                                        : sub.locked
                                          ? "bg-gray-400"
                                          : "bg-editor-blue",
                                    getSubcircleShapeClass(sub.icon),
                                    getCheckpointBorderClass(sub),
                                    "w-7 h-7 flex-none flex items-center justify-center text-white font-extrabold text-[10px]"
                                  )}
                                >
                                  <span className={sub.icon?.toLowerCase() === "code" ? "-rotate-45" : ""}>
                                    {group.group_index}.{sub.stage}
                                  </span>
                                </span>
                                <span className="subChapterName break-words whitespace-normal min-w-0 flex-1">
                                  {localizeCircleFlowText(sub.title)}
                                </span>
                              </li>
                            ))}
                          </ol>
                        )}
                      {group.subcircles.length > 1 && (
                        <FaChevronDown
                          onClick={() => toggleOpenGroup(group.group_index)}
                          className={classNames(
                            openedGroups.includes(group.group_index) ? "rotate-180" : "",
                            "absolute right-5 top-6 text-editor-title dark:text-white cursor-pointer hover:text-opacity-80 duration-300"
                          )}
                        />
                      )}
                    </div>
                  );
                })
              )}
            </ol>
          </motion.div>
        )}
        <div className="bg-white rounded-b-[30px] flex justify-between absolute z-20 items-center bottom-0 left-0 px-4 py-4 w-full dark:bg-vs-dark-background-lighter">
          {currentFlatIndex > 0 ? (
            <button
              onClick={goToPrevious}
              disabled={pendingNavigateIndex !== null}
              className="h-8 text-white duration-300 flex items-center justify-center bg-[#A7BBD4] py-1 px-2 cursor-pointer rounded-full"
            >
              <BiChevronLeft size={30} />
            </button>
          ) : (
            <div />
          )}
          {currentFlatIndex >= 0 && currentFlatIndex < flatSubcircles.length - 1 ? (
            <button
              onClick={goToNext}
              disabled={pendingNavigateIndex !== null}
              className="flex text-white items-center justify-center duration-300 bg-[#059BCA] py-1 px-3 pl-5 cursor-pointer rounded-full"
            >
              <span>{t("buttonNext")}</span>
              <BiChevronRight size={30} />
            </button>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </motion.div>
    </div>
  );
};
